// ------------------------------------------------------------
// PreferencesStep.tsx – Family-wide preferences
// ------------------------------------------------------------
import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  InputAdornment,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Collapse,
} from '@mui/material';

type Props = {
  onBack?: () => void;
  onNext: (data: PreferencesData) => void;
};

export type PreferencesData = {
  max_prep_time: number | null;
  likes_leftovers: boolean;
  leftover_frequency: number;
  meal_plan_start_day: number;
};

// Leftover frequency options (percentage-based).
// "Never" is intentionally absent — the likes_leftovers toggle handles
// the off-state. Matches mobile.
const leftoverFrequencyOptions = [
  { value: 33, label: 'Sometimes', description: 'About 1-2 dinners per week' },
  { value: 50, label: 'Half the time', description: 'Every other dinner' },
  { value: 75, label: 'Most of the time', description: 'Most dinners' },
  { value: 100, label: 'Always', description: 'Every dinner' },
];

const PreferencesStep: React.FC<Props> = ({ onBack, onNext }) => {
  const [maxPrepTime, setMaxPrepTime] = useState<string>('45');
  const [likesLeftovers, setLikesLeftovers] = useState(true);
  const [leftoverFrequency, setLeftoverFrequency] = useState<number>(50); // Default 50% (half the time)
  const [mealPlanStartDay, setMealPlanStartDay] = useState<number>(1); // Default to Monday
  const [error, setError] = useState('');

  const daysOfWeek = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
  ];

  const handleNext = () => {
    setError('');

    // Validate prep time (required)
    const prepTimeNum = parseInt(maxPrepTime);
    if (!maxPrepTime || isNaN(prepTimeNum) || prepTimeNum < 5 || prepTimeNum > 180) {
      setError('Please enter a prep time between 5 and 180 minutes');
      return;
    }

    onNext({
      max_prep_time: prepTimeNum,
      likes_leftovers: likesLeftovers,
      leftover_frequency: likesLeftovers ? leftoverFrequency : 0,
      meal_plan_start_day: mealPlanStartDay,
    });
  };

  return (
    <Box maxWidth={600} mx="auto">
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Family Preferences
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Help us customize your meal plans to fit your family's needs
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Max Prep Time */}
          <TextField
            fullWidth
            label="Maximum Prep Time"
            type="number"
            value={maxPrepTime}
            onChange={(e) => setMaxPrepTime(e.target.value)}
            required
            InputProps={{
              endAdornment: <InputAdornment position="end">minutes</InputAdornment>,
            }}
            helperText="How much time can you spend preparing meals? (5-180 minutes)"
            sx={{ mb: 3 }}
          />

          {/* Meal Plan Start Day */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="meal-plan-start-day-label">Meal Plan Start Day</InputLabel>
            <Select
              labelId="meal-plan-start-day-label"
              id="meal-plan-start-day"
              value={mealPlanStartDay}
              label="Meal Plan Start Day"
              onChange={(e) => setMealPlanStartDay(e.target.value as number)}
            >
              {daysOfWeek.map((day) => (
                <MenuItem key={day.value} value={day.value}>
                  {day.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Choose the day your weekly meal plans should start. We recommend selecting the day after you typically do your grocery shopping.
            </FormHelperText>
          </FormControl>

          {/* Leftovers */}
          <FormControlLabel
            control={
              <Switch
                checked={likesLeftovers}
                onChange={(e) => {
                  setLikesLeftovers(e.target.checked);
                  if (!e.target.checked) setLeftoverFrequency(0);
                  else if (leftoverFrequency === 0) setLeftoverFrequency(50);
                }}
              />
            }
            label={
              <Box>
                <Typography variant="body1">
                  I like leftovers
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  We'll plan extra portions so you can enjoy easy meals later
                </Typography>
              </Box>
            }
            sx={{ mb: 2 }}
          />

          {/* Leftover Frequency - shown only when likesLeftovers is true */}
          <Collapse in={likesLeftovers}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="leftover-frequency-label">When cooking dinner, how often make extra?</InputLabel>
              <Select
                labelId="leftover-frequency-label"
                id="leftover-frequency"
                value={leftoverFrequency}
                label="When cooking dinner, how often make extra?"
                onChange={(e) => setLeftoverFrequency(e.target.value as number)}
              >
                {leftoverFrequencyOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label} - {option.description}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Extra portions become easy leftover meals for your next lunch or dinner
              </FormHelperText>
            </FormControl>
          </Collapse>

          {/* Navigation Buttons */}
          <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
            {onBack && (
              <Button onClick={onBack} variant="outlined" fullWidth>
                Back
              </Button>
            )}
            <Button onClick={handleNext} variant="contained" fullWidth>
              Continue
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PreferencesStep;
